/**
 * Kaksos Portal - Claim Page (Loop 1 cut 2, MEMBER-TOKEN-PORTAL-CARRY)
 *
 * Public, no login. A circle member opens the claim link the owner issued
 * (cut 1: POST /api/connections/members/:membershipId/issue-link). This page
 * hands the token to the API exactly once, stores the returned record on this
 * phone under the Kaksos id the API returned (Ruling A: never derived from a
 * person id), and scrubs the token out of the address bar and history.
 *
 * WISDOM 19/22: the token is a credential. Never logged, never echoed, never
 * retried. A non-200 stores nothing and shows the API's plain message.
 * No auth context, no reads of the owner's boby_kaksos_* keys.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { KAKSOS_API_URL, setMemberToken } from '../lib/api';

type ClaimState =
    | { status: 'missing' }
    | { status: 'claiming' }
    | { status: 'claimed'; circleLevel: string; telepathcode: string | null; kaksosName: string | null; memberName: string | null }
    | { status: 'failed'; message: string };

const CIRCLE_NAMES: Record<string, string> = {
    center: 'centre',
    inner: 'inner circle',
    mid: 'mid circle',
    outer: 'outer circle',
    network: 'network',
};

function circleName(level: string): string {
    return CIRCLE_NAMES[level] || level;
}

export default function ClaimPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');
    const [state, setState] = useState<ClaimState>(token ? { status: 'claiming' } : { status: 'missing' });
    // One POST per page load, even under React StrictMode's double-invoked effects.
    const claimFired = useRef(false);

    useEffect(() => {
        if (!token || claimFired.current) return;
        claimFired.current = true;

        async function claim() {
            try {
                const response = await fetch(`${KAKSOS_API_URL}/api/public/claim-connection`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token }),
                });
                const data = await response.json().catch(() => ({}));
                // The token leaves the address bar and history on every outcome (fail-closed).
                window.history.replaceState(null, '', '/claim');
                if (response.status === 200 && data.status === 'claimed' && data.boby_place_id) {
                    // Loop 1 cut 4 (A5): the cut 3 reply fields; each is null when absent or empty.
                    const telepathcode = typeof data.telepathcode === 'string' && data.telepathcode ? data.telepathcode : null;
                    const kaksosName = typeof data.kaksos_name === 'string' && data.kaksos_name ? data.kaksos_name : null;
                    const memberName = typeof data.member_name === 'string' && data.member_name ? data.member_name : null;
                    setMemberToken(data.boby_place_id, {
                        memberToken: token as string,
                        kaksosId: data.boby_place_id,
                        circleLevel: data.circle_level,
                        expiresAt: data.token_expires_at,
                        ...(memberName ? { memberName } : {}),
                    });
                    setState({ status: 'claimed', circleLevel: data.circle_level, telepathcode, kaksosName, memberName });
                } else {
                    setState({ status: 'failed', message: data.error || 'This link could not be claimed.' });
                }
            } catch {
                window.history.replaceState(null, '', '/claim');
                setState({ status: 'failed', message: 'Could not reach the Kaksos. Please try again later.' });
            }
        }

        claim();
    }, [token]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-amber-50 to-yellow-100 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                <img src="/Bobylogo.png" alt="BOBY" className="w-16 h-16 mx-auto mb-6 object-contain" />

                {state.status === 'missing' && (
                    <>
                        <h1 className="text-2xl font-bold text-gray-800 mb-2">This link is missing its key</h1>
                        <p className="text-gray-600">Ask the person who sent it for a fresh link.</p>
                    </>
                )}

                {state.status === 'claiming' && (
                    <>
                        <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                        <p className="text-gray-600">Opening your link...</p>
                    </>
                )}

                {state.status === 'claimed' && (
                    <>
                        <h1 className="text-2xl font-bold text-gray-800 mb-2">You are in</h1>
                        <p className="text-gray-700 mb-4">
                            You are in the <span className="font-semibold">{circleName(state.circleLevel)}</span>.
                        </p>
                        {state.telepathcode ? (
                            <button
                                type="button"
                                onClick={() => navigate(`/chat/${state.telepathcode}`)}
                                className="w-full py-3 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 transition-colors"
                            >
                                {state.kaksosName ? `Open ${state.kaksosName}` : 'Open the chat'}
                            </button>
                        ) : (
                            <p className="text-gray-600">Open the chat link you were given.</p>
                        )}
                    </>
                )}

                {state.status === 'failed' && (
                    <>
                        <h1 className="text-2xl font-bold text-gray-800 mb-2">This link did not open</h1>
                        <p className="text-gray-600">{state.message}</p>
                    </>
                )}
            </div>
        </div>
    );
}
